/**
 * Chart of Accounts Initializer & Repair Utility
 *
 * This utility handles:
 * 1. Initializing default COA for new companies
 * 2. Repairing/fixing COA for existing companies
 * 3. Validating parent accounts exist before entity creation
 * 4. Creating linked accounts for customers/vendors/banks
 */

import { collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, query, where, limit, writeBatch, orderBy, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { DEFAULT_CHART_OF_ACCOUNTS, LINKED_ACCOUNT_PARENTS, generateLevel5Code } from '../data/defaultChartOfAccounts';
import { ensureOpeningBalanceTransaction } from './transactionHelper';
import { invalidateMaster, getAccountsCached } from './masterDataStore';
import { clearSystemAccountMisses } from './subTransactionHelper';

/**
 * Initialize Chart of Accounts for a new company
 * Creates all Level 1-4 system accounts and essential Level 5 accounts
 *
 * @param {string} companyId - The company ID
 * @param {string} userId - The user creating the accounts
 * @returns {Promise<{success: boolean, message: string, accountsCreated: number}>}
 */
export const initializeChartOfAccounts = async (companyId, userId) => {
  try {
    const accountsRef = collection(db, 'companies', companyId, 'accounts');

    // Check if COA already exists
    const existingSnapshot = await getDocs(accountsRef);
    if (!existingSnapshot.empty) {
      return {
        success: false,
        message: 'Chart of Accounts already exists for this company',
        accountsCreated: 0
      };
    }

    // Create a map to store created account IDs by code
    const accountIdMap = {};
    const batch = writeBatch(db);
    let accountsCreated = 0;
    const timestamp = new Date().toISOString();

    // Process accounts in order (Level 1 first, then 2, 3, 4, 5)
    const sortedAccounts = [...DEFAULT_CHART_OF_ACCOUNTS].sort((a, b) => a.level - b.level);

    for (const account of sortedAccounts) {
      const docRef = doc(accountsRef);

      // Find parent ID from our map
      let parentId = null;
      if (account.parentCode) {
        parentId = accountIdMap[account.parentCode];
      }

      // Build path array
      const path = [];
      if (parentId) {
        // Get parent's path and add parent to it
        const parentAccount = sortedAccounts.find(a => a.code === account.parentCode);
        if (parentAccount && parentAccount.parentCode) {
          // Recursively build path
          let currentCode = account.parentCode;
          while (currentCode) {
            if (accountIdMap[currentCode]) {
              path.unshift(accountIdMap[currentCode]);
            }
            const current = sortedAccounts.find(a => a.code === currentCode);
            currentCode = current?.parentCode;
          }
        } else if (parentId) {
          path.push(parentId);
        }
      }

      const accountData = {
        code: account.code,
        name: account.name,
        level: account.level,
        accountType: account.accountType,
        balanceType: account.balanceType,
        parentId: parentId,
        path: path,
        isSystemAccount: account.isSystemAccount || false,
        isPostable: account.isPostable || false,
        isActive: true,
        description: account.description || '',
        linkedEntityType: account.linkedEntityType || null,
        allowUserAccounts: account.allowUserAccounts !== false,
        openingBalance: 0,
        currentBalance: 0,
        createdAt: timestamp,
        updatedAt: timestamp,
        createdBy: userId
      };

      batch.set(docRef, accountData);
      accountIdMap[account.code] = docRef.id;
      accountsCreated++;
    }

    await batch.commit();

    // Give every new company a ready Walk-in Customer receivable account from
    // day one, so a Sale Invoice / POS receipt to Walk-in never fails to balance.
    try {
      await ensureWalkInCustomerAccount(companyId, userId);
    } catch (e) {
      console.warn('Walk-in account setup skipped during init:', e?.message || e);
    }

    return {
      success: true,
      message: `Chart of Accounts initialized successfully with ${accountsCreated} accounts`,
      accountsCreated
    };
  } catch (error) {
    console.error('Error initializing COA:', error);
    return {
      success: false,
      message: `Error initializing COA: ${error.message}`,
      accountsCreated: 0
    };
  }
};

/**
 * Repair Chart of Accounts.
 *
 * Two jobs:
 *   1. Re-create any of the standard accounts that have gone missing.
 *   2. Put accounts that DO exist back in their correct place — tree position
 *      (`path`), parent, level, account type, balance side, postable and active
 *      flags. This second job is the important one: an account with a broken
 *      `path` still posts correctly but is INVISIBLE in the Chart of Accounts
 *      screen (the tree groups by path), and a level-5 account without
 *      `isPostable` is silently dropped from the Balance Sheet.
 *
 * It also sweeps customer/vendor/bank/staff accounts, which are created on the
 * fly and appear nowhere in the seed, and reports problems it must not fix by
 * itself (duplicate codes, re-created accounts whose old history is orphaned).
 *
 * Never deletes anything. Never merges anything. Never renames an account whose
 * name the user deliberately changed (`nameCustomized`).
 *
 * @param {string} companyId - The company ID
 * @param {string} userId - The user performing the repair
 * @param {{ dryRun?: boolean }} [options] - dryRun reports what WOULD change and writes nothing
 * @returns {Promise<{success, message, accountsAdded, accountsFixed, created, renamed, changes, warnings, dryRun}>}
 */
export const repairChartOfAccounts = async (companyId, userId, options = {}) => {
  const dryRun = options.dryRun === true;
  // Speed-cache safety: forget "accounts known to be missing" so the next save
  // looks them up again after the repair creates them.
  if (!dryRun) clearSystemAccountMisses(companyId);

  try {
    const accountsRef = collection(db, 'companies', companyId, 'accounts');
    const existingSnapshot = await getDocs(accountsRef);

    // Build map of existing accounts by code
    const existingAccounts = {};
    const existingIds = {};
    const byCode = new Map();   // code -> [accounts]  (duplicate detection)
    const allAccounts = [];
    existingSnapshot.docs.forEach(d => {
      const data = d.data();
      const record = { id: d.id, ...data };
      const code = String(data.code ?? '');
      allAccounts.push(record);
      if (!byCode.has(code)) byCode.set(code, []);
      byCode.get(code).push(record);
      existingAccounts[code] = record;
      existingIds[code] = d.id;
    });

    const timestamp = new Date().toISOString();
    const creates = [];    // { ref, data, label }
    const updates = [];    // { ref, data, label, reasons }
    const warnings = [];
    const renamed = [];
    const created = [];
    // Corrected paths for accounts fixed in THIS run, so a child repaired later
    // inherits the fixed breadcrumb instead of copying its parent's broken one.
    const pendingPath = new Map();

    // Sort by level to ensure parents are processed first
    const sortedDefaults = [...DEFAULT_CHART_OF_ACCOUNTS].sort((a, b) => a.level - b.level);
    const seedByCode = new Map(sortedDefaults.map(a => [a.code, a]));

    // Walk the parentCode chain to build the breadcrumb of ancestor IDs.
    const buildSeedPath = (parentCode) => {
      const path = [];
      const guard = new Set();
      let currentCode = parentCode;
      while (currentCode && !guard.has(currentCode)) {
        guard.add(currentCode);
        if (existingIds[currentCode]) path.unshift(existingIds[currentCode]);
        currentCode = seedByCode.get(currentCode)?.parentCode;
      }
      return path;
    };

    const samePath = (a, b) =>
      Array.isArray(a) && a.length === b.length && a.every((v, i) => v === b[i]);

    // Seed codes Repair must NOT touch in this company (see `createOnly` below).
    const blockedSeedCodes = new Set();

    // ---------- 1. The 149 standard accounts ----------
    for (const defaultAccount of sortedDefaults) {
      const existing = existingAccounts[defaultAccount.code];

      // A child of a blocked createOnly account is never created or modified —
      // it would otherwise be filed under the company's unrelated account.
      if (defaultAccount.parentCode && blockedSeedCodes.has(defaultAccount.parentCode)) {
        blockedSeedCodes.add(defaultAccount.code);
        continue;
      }

      // `createOnly` seeds were added AFTER many companies already existed, so a
      // company may have made its OWN account at the same code (e.g. the next
      // free slot under a group). Repair normally corrects/renames whatever sits
      // at a seed code — for these that would hijack the company's account and
      // its history. So: create only when the code is free; if it is taken by a
      // different account, leave it completely alone and say so.
      if (defaultAccount.createOnly && existing) {
        // `createOnlyStrict`: only an account Repair itself created (system flag)
        // counts as ours — a company's own account at that code is left alone
        // even if it carries the same name.
        const sameAccount = existing.isSystemAccount === true ||
          (!defaultAccount.createOnlyStrict &&
            String(existing.name || '').trim().toLowerCase() === String(defaultAccount.name).trim().toLowerCase());
        if (!sameAccount) {
          blockedSeedCodes.add(defaultAccount.code);
          warnings.push(
            `Code ${defaultAccount.code} is already used by your account "${existing.name}", so the standard ` +
            `"${defaultAccount.name}" account was NOT added (your account was left untouched). ` +
            (defaultAccount.blockedNote ||
              `If you need it, create a "${defaultAccount.name}" expense account yourself — receipts will find it by name.`)
          );
          continue;
        }
      }
      const expectedPath = buildSeedPath(defaultAccount.parentCode);
      const expectedParentId = defaultAccount.parentCode
        ? (existingIds[defaultAccount.parentCode] || null)
        : null;

      if (!existing) {
        // Account doesn't exist - create it
        const docRef = doc(accountsRef);

        const accountData = {
          code: defaultAccount.code,
          name: defaultAccount.name,
          level: defaultAccount.level,
          accountType: defaultAccount.accountType,
          balanceType: defaultAccount.balanceType,
          parentId: expectedParentId,
          path: expectedPath,
          isSystemAccount: defaultAccount.isSystemAccount || false,
          isPostable: defaultAccount.isPostable || false,
          isActive: true,
          description: defaultAccount.description || '',
          linkedEntityType: defaultAccount.linkedEntityType || null,
          allowUserAccounts: defaultAccount.allowUserAccounts !== false,
          openingBalance: 0,
          currentBalance: 0,
          createdAt: timestamp,
          updatedAt: timestamp,
          createdBy: userId
        };

        creates.push({ ref: docRef, data: accountData, label: `${defaultAccount.code} ${defaultAccount.name}` });
        existingIds[defaultAccount.code] = docRef.id;
        pendingPath.set(docRef.id, expectedPath);
        created.push(`${defaultAccount.code} — ${defaultAccount.name}`);
        continue;
      }

      const upd = {};
      const reasons = [];

      // --- Structure. This is what makes an account visible in the tree and
      //     counted on the Balance Sheet, and nothing else repairs it. ---
      if (!samePath(existing.path, expectedPath)) {
        upd.path = expectedPath;
        reasons.push('tree position');
      }
      if ((existing.parentId || null) !== expectedParentId) {
        upd.parentId = expectedParentId;
        reasons.push('parent');
      }
      if (existing.level !== defaultAccount.level) {
        upd.level = defaultAccount.level;
        reasons.push('level');
      }
      if (existing.accountType !== defaultAccount.accountType) {
        upd.accountType = defaultAccount.accountType;
        reasons.push('account type');
      }
      if (existing.balanceType !== defaultAccount.balanceType) {
        upd.balanceType = defaultAccount.balanceType;
        reasons.push('balance side');
      }
      if (existing.isPostable !== !!defaultAccount.isPostable) {
        upd.isPostable = !!defaultAccount.isPostable;
        reasons.push('postable flag');
      }
      // Standard accounts stay active by design — automated postings target them
      // whether or not somebody switched them off (the UI hides that toggle).
      if (existing.isActive === false) {
        upd.isActive = true;
        reasons.push('re-activated');
      }

      // --- Wiring, only for accounts the seed marks as system ---
      if (defaultAccount.isSystemAccount) {
        // Backfill the system flag if missing (legacy companies seeded before it existed).
        if (!existing.isSystemAccount) {
          upd.isSystemAccount = true;
          reasons.push('system flag');
        }

        // Keep the entity-link wiring in sync with the seed (e.g. when we add a
        // new linked entity type like 'staff' to an L4 parent).
        const seedLinkedEntity = defaultAccount.linkedEntityType || null;
        if ((existing.linkedEntityType || null) !== seedLinkedEntity) {
          upd.linkedEntityType = seedLinkedEntity;
          reasons.push('entity link');
        }
        const seedAllow = defaultAccount.allowUserAccounts !== false;
        if ((existing.allowUserAccounts !== false) !== seedAllow) {
          upd.allowUserAccounts = seedAllow;
          reasons.push('sub-account rule');
        }

        // Propagate seed renames (e.g. Trade Debtors → Receivable Customers) —
        // but NEVER over a name the user deliberately changed. Repair used to
        // silently wipe those on every click.
        if (existing.name !== defaultAccount.name && existing.nameCustomized !== true) {
          upd.name = defaultAccount.name;
          renamed.push(`${defaultAccount.code}: "${existing.name}" → "${defaultAccount.name}"`);
          reasons.push('name reset to standard');
        }
        if (existing.nameCustomized !== true &&
            (existing.description || '') !== (defaultAccount.description || '')) {
          upd.description = defaultAccount.description || '';
        }
      }

      if (Object.keys(upd).length > 0) {
        upd.updatedAt = timestamp;
        updates.push({
          ref: doc(accountsRef, existing.id),
          data: upd,
          label: `${defaultAccount.code} ${existing.name}`,
          reasons
        });
      }
      if (upd.path) pendingPath.set(existing.id, expectedPath);
    }

    // ---------- 2. Party accounts (customer / vendor / bank / staff) ----------
    // These are created during normal data entry and appear nowhere in the seed,
    // so until now nothing repaired them — yet they are the ones most likely to
    // be sitting outside the tree.
    const accountById = new Map(allAccounts.map(a => [a.id, a]));
    for (const acc of allAccounts) {
      if (!acc.linkedEntityType || !acc.linkedEntityId) continue;
      if (seedByCode.has(String(acc.code ?? ''))) continue;   // handled above

      let parent = acc.parentId ? accountById.get(acc.parentId) : null;
      if (!parent) {
        const fallbackCode = LINKED_ACCOUNT_PARENTS[acc.linkedEntityType];
        parent = fallbackCode ? existingAccounts[fallbackCode] : null;
      }
      if (!parent) {
        warnings.push(`${acc.code || '(no code)'} "${acc.name}": its ${acc.linkedEntityType} group is missing — skipped.`);
        continue;
      }

      const parentPath = pendingPath.get(parent.id) || parent.path || [];
      const expectedPath = [...parentPath, parent.id];

      const upd = {};
      const reasons = [];
      if (!samePath(acc.path, expectedPath)) { upd.path = expectedPath; reasons.push('tree position'); }
      if (acc.parentId !== parent.id) { upd.parentId = parent.id; reasons.push('parent'); }
      if (acc.isPostable !== true) { upd.isPostable = true; reasons.push('postable flag'); }
      if (acc.level !== 5) { upd.level = 5; reasons.push('level'); }

      if (Object.keys(upd).length > 0) {
        upd.updatedAt = timestamp;
        updates.push({
          ref: doc(accountsRef, acc.id),
          data: upd,
          label: `${acc.code || ''} ${acc.name}`.trim(),
          reasons
        });
      }
    }

    // ---------- 3. Things Repair must report, not silently "fix" ----------
    for (const [code, list] of byCode) {
      if (list.length > 1) {
        warnings.push(
          `Duplicate code ${code} — ${list.length} accounts share it (${list.map(a => a.name).join(' / ')}). ` +
          `That party's ledger is split in two. Merge them by hand.`
        );
      }
    }
    if (created.length > 0) {
      warnings.push(
        `${created.length} standard account(s) ${dryRun ? 'will be' : 'were'} re-created EMPTY. ` +
        `If they were deleted after entries had been posted to them, that old history stays orphaned ` +
        `(it does not move to the new account). Check the Trial Balance afterwards.`
      );
    }

    // ---------- 4. Dry run stops here, having written nothing ----------
    const changes = updates.map(u => `${u.label}: ${u.reasons.join(', ')}`);
    if (dryRun) {
      // The Walk-in account lives outside the seed, so it has to be inspected
      // separately — otherwise a company whose ONLY problem is Walk-in would be
      // told "nothing to repair".
      let walkInAdded = 0;
      let walkInFixed = 0;
      try {
        const w = await ensureWalkInCustomerAccount(companyId, userId, { dryRun: true });
        walkInAdded = w.added;
        walkInFixed = w.fixed;
        if (w.added > 0) created.push('Walk-in Customer receivable account');
        if (w.fixed > 0) changes.push('Walk-in Customer account: tree position / postable flag');
        if (w.customerCreated) warnings.push('No Walk-in Customer exists — one will be created.');
        if (w.customerFixed) warnings.push('The Walk-in Customer record is missing its marker — will be fixed.');
        if (!w.linked) warnings.push('Walk-in Customer account cannot be placed: the Receivable Customers group (11201) is missing.');
      } catch (e) {
        warnings.push(`Could not check the Walk-in Customer account: ${e?.message || e}`);
      }

      return {
        success: true,
        dryRun: true,
        message: `Repair preview — will add ${creates.length + walkInAdded}, will fix ${updates.length + walkInFixed}.`,
        accountsAdded: creates.length + walkInAdded,
        accountsFixed: updates.length + walkInFixed,
        created,
        renamed,
        changes,
        warnings
      };
    }

    // ---------- 5. Commit, chunked ----------
    // A company with thousands of customers can easily exceed Firestore's
    // 500-operations-per-batch limit now that party accounts are swept too.
    const ops = [
      ...creates.map(c => ({ kind: 'set', ref: c.ref, data: c.data })),
      ...updates.map(u => ({ kind: 'update', ref: u.ref, data: u.data }))
    ];
    for (let i = 0; i < ops.length; i += 400) {
      const batch = writeBatch(db);
      ops.slice(i, i + 400).forEach(op => {
        if (op.kind === 'set') batch.set(op.ref, op.data);
        else batch.update(op.ref, op.data);
      });
      await batch.commit();
    }

    let accountsAdded = creates.length;
    let accountsFixed = updates.length;

    // Walk-in Customer account is tied to a customer record, so it lives outside
    // the seed loop above. A missing/unlinked one is exactly what makes a Sale
    // Invoice to "Walk-in Customer" fail to balance — so repair it here too.
    // A failure here used to be swallowed into the console while the user was
    // still told "completed"; now it is reported.
    try {
      const w = await ensureWalkInCustomerAccount(companyId, userId);
      accountsAdded += w.added;
      accountsFixed += w.fixed;
      if (w.customerCreated) warnings.push('No Walk-in Customer existed — one was created.');
      if (w.customerFixed) warnings.push('The Walk-in Customer record was missing its marker — fixed.');
      if (!w.linked) warnings.push('Walk-in Customer account could not be placed: the Receivable Customers group (11201) is missing.');
    } catch (e) {
      warnings.push(`Walk-in Customer account could NOT be repaired: ${e?.message || e}`);
    }

    // Repair wrote new/updated accounts straight to Firestore, but every form
    // reads accounts from the device cache (0-reads optimisation). Without this,
    // an account Repair just created (e.g. WHT Payable) stays invisible to the
    // running app until a hard reload — so the very Sale Invoice the user came
    // here to fix would keep failing to balance. Refresh the cache now.
    try { invalidateMaster(companyId, 'accounts'); } catch (e) { /* non-fatal */ }

    let message = `COA repair completed. Added: ${accountsAdded}, Fixed: ${accountsFixed}`;
    if (renamed.length > 0) {
      message += `\n\n${renamed.length} account name(s) reset to standard:\n` + renamed.join('\n');
    }
    if (warnings.length > 0) {
      message += `\n\nPlease note:\n• ` + warnings.join('\n• ');
    }

    return {
      success: true,
      dryRun: false,
      message,
      accountsAdded,
      accountsFixed,
      created,
      renamed,
      changes,
      warnings
    };
  } catch (error) {
    console.error('Error repairing COA:', error);
    return {
      success: false,
      dryRun,
      message: `Error repairing COA: ${error.message}`,
      accountsAdded: 0,
      accountsFixed: 0,
      created: [],
      renamed: [],
      changes: [],
      warnings: []
    };
  }
};

/**
 * Ensure the "Walk-in Customer" has a proper, linked, tree-placed receivable
 * account under 11201 (Receivable Customers).
 *
 * WHY THIS EXISTS: the walk-in account is tied to a customer record, so it lives
 * OUTSIDE the fixed seed that repairChartOfAccounts() rebuilds. If a company bills
 * a Sale Invoice / POS receipt to "Walk-in Customer" but this account is missing
 * or unlinked, the customer side of the entry silently drops and the invoice
 * fails to balance ("Debits ≠ Credits"). It also wouldn't show in the COA tree
 * because it lacked a `path`. This one function fixes all of that.
 *
 * It is deliberately idempotent and non-destructive: it links to whatever
 * walk-in customer already exists (by flag, then by name) rather than creating a
 * duplicate, and only creates the account when genuinely absent.
 *
 * @param {string} companyId
 * @param {string} userId
 * @param {{ dryRun?: boolean }} [options] - dryRun reports what WOULD change and writes nothing
 * @returns {Promise<{added, fixed, customerCreated, customerFixed, linked}>}
 */
export const ensureWalkInCustomerAccount = async (companyId, userId, options = {}) => {
  const dryRun = options.dryRun === true;
  const WALKIN_ID = 'WALKIN_CUSTOMER';
  const WALKIN_ACCOUNT_ID = 'WALKIN_ACCOUNT';
  const WALKIN_ACCOUNT_CODE = '11201999';
  const PARENT_CODE = LINKED_ACCOUNT_PARENTS.customer || '11201';
  const timestamp = new Date().toISOString();

  const customersRef = collection(db, 'companies', companyId, 'customers');
  const accountsRef = collection(db, 'companies', companyId, 'accounts');

  // These count ACCOUNT changes only. Changes to the customer record itself are
  // reported separately — counting them as "accounts fixed" made the result
  // message misleading.
  let added = 0;
  let fixed = 0;
  let customerCreated = false;
  let customerFixed = false;

  // 1. Locate the walk-in customer as cheaply as possible — the indexed flag
  //    first, then the deterministic id, and only as a last resort a full scan
  //    by name. The old code always read the ENTIRE customers collection, which
  //    on a company with thousands of customers is thousands of reads on every
  //    repair (and repair also runs by itself during customer/vendor saves).
  let matchDoc = null;

  const flagged = await getDocs(query(customersRef, where('isWalkIn', '==', true), limit(1)));
  if (!flagged.empty) {
    matchDoc = flagged.docs[0];
  } else {
    const fixedIdCustomer = await getDoc(doc(customersRef, WALKIN_ID));
    if (fixedIdCustomer.exists()) {
      matchDoc = fixedIdCustomer;
    } else {
      // Legacy record with neither the flag nor the fixed id — only now do we
      // pay for a full scan, and only to match on name.
      const isWalkinName = (c) =>
        /walk[\s-]*in/.test((c.companyName || c.customerName || c.name || '').toLowerCase());
      const custSnap = await getDocs(customersRef);
      matchDoc = custSnap.docs.find(d => isWalkinName(d.data())) || null;
    }
  }

  let walkinCustomerId;
  if (matchDoc) {
    walkinCustomerId = matchDoc.id;
    // Backfill the flag so future lookups stay on the cheap path above.
    if (matchDoc.data().isWalkIn !== true) {
      if (!dryRun) {
        await updateDoc(doc(customersRef, matchDoc.id), { isWalkIn: true, updatedAt: timestamp });
      }
      customerFixed = true;
    }
  } else {
    if (!dryRun) {
      await setDoc(doc(customersRef, WALKIN_ID), {
        companyName: 'Walk-in Customer',
        isWalkIn: true, isActive: true,
        phone: '', email: '', address: '', city: '',
        creditLimit: 0, openingBalance: 0,
        createdAt: timestamp, createdBy: userId
      }, { merge: true });
    }
    walkinCustomerId = WALKIN_ID;
    customerCreated = true;
  }

  // 2. Find the receivable parent (11201). If it's missing the main repair
  //    should have created it; bail quietly rather than guessing.
  const parentSnap = await getDocs(query(accountsRef, where('code', '==', PARENT_CODE)));
  if (parentSnap.empty) return { added, fixed, customerCreated, customerFixed, linked: false };
  const parent = { id: parentSnap.docs[0].id, ...parentSnap.docs[0].data() };
  const parentPath = [...(parent.path || []), parent.id];

  // Store the REVERSE pointer on the customer record (customer → account). The
  // account already points at the customer (linkedEntityId), but the receipts /
  // payments screens read customer.linkedAccountId to find the account to post
  // to. The Walk-in customer never had this written, so a cash/bank receipt to
  // Walk-in failed with "accountId: undefined". This makes Repair COA fix it for
  // good, on top of the runtime fallback those screens now have.
  const backfillCustomerPointer = async (accId, accCode) => {
    if (dryRun || !accId) return;
    const cur = matchDoc ? matchDoc.data() : {};
    if (cur.linkedAccountId === accId && (cur.linkedAccountCode || '') === (accCode || '')) return;
    await updateDoc(doc(customersRef, walkinCustomerId), {
      linkedAccountId: accId,
      linkedAccountCode: accCode || '',
      updatedAt: timestamp
    });
    if (!customerCreated) customerFixed = true;
  };

  // 3. Is an account already LINKED to this walk-in customer? If so, only repair
  //    its tree fields (path/parent/active) so it posts and shows correctly.
  const linkedSnap = await getDocs(query(
    accountsRef,
    where('linkedEntityType', '==', 'customer'),
    where('linkedEntityId', '==', walkinCustomerId)
  ));
  if (!linkedSnap.empty) {
    const acc = { id: linkedSnap.docs[0].id, ...linkedSnap.docs[0].data() };
    const updates = {};
    if (!Array.isArray(acc.path) || acc.path.length === 0) updates.path = parentPath;
    if (!acc.parentId) updates.parentId = parent.id;
    if (acc.isActive === false) updates.isActive = true;
    // Without this flag the Balance Sheet counts a level-5 account's own balance
    // as zero, so Walk-in receivables silently vanished from it.
    if (acc.isPostable !== true) updates.isPostable = true;
    if (acc.level !== 5) updates.level = 5;
    if (Object.keys(updates).length > 0) {
      updates.updatedAt = timestamp;
      if (!dryRun) await updateDoc(doc(accountsRef, acc.id), updates);
      fixed++;
    }
    await backfillCustomerPointer(acc.id, acc.code);
    return { added, fixed, customerCreated, customerFixed, linked: true };
  }

  // 4. No linked account. The fixed-id account may exist but be unlinked — link
  //    it rather than making a second one.
  const fixedIdSnap = await getDoc(doc(accountsRef, WALKIN_ACCOUNT_ID));
  if (fixedIdSnap.exists()) {
    if (!dryRun) {
      await updateDoc(doc(accountsRef, WALKIN_ACCOUNT_ID), {
        linkedEntityType: 'customer',
        linkedEntityId: walkinCustomerId,
        parentId: parent.id,
        parentCode: parent.code,
        path: parentPath,
        level: 5,
        isPostable: true,
        isActive: true,
        updatedAt: timestamp
      });
    }
    fixed++;
    await backfillCustomerPointer(WALKIN_ACCOUNT_ID, fixedIdSnap.data().code || WALKIN_ACCOUNT_CODE);
    return { added, fixed, customerCreated, customerFixed, linked: true };
  }

  // 5. Create it fresh — mirrors a normal linked customer account (inherits the
  //    parent's type/balance side) so it looks identical to its siblings.
  if (dryRun) {
    added++;
    return { added, fixed, customerCreated, customerFixed, linked: true };
  }

  await setDoc(doc(accountsRef, WALKIN_ACCOUNT_ID), {
    code: WALKIN_ACCOUNT_CODE,
    name: 'Walk-in Customer',
    level: 5,
    parentId: parent.id,
    parentCode: parent.code,
    path: parentPath,
    accountType: parent.accountType,
    balanceType: parent.balanceType,
    openingBalance: 0,
    currentBalance: 0,
    linkedEntityType: 'customer',
    linkedEntityId: walkinCustomerId,
    // isPostable is NOT optional: the Balance Sheet only counts a level-5
    // account's own balance when this is true, so leaving it off made Walk-in
    // receivables show as zero there while the Trial Balance showed them.
    isPostable: true,
    isSystemAccount: false,
    isActive: true,
    description: 'Linked customer account for Walk-in Customer',
    createdAt: timestamp,
    updatedAt: timestamp,
    createdBy: userId
  });
  added++;
  await backfillCustomerPointer(WALKIN_ACCOUNT_ID, WALKIN_ACCOUNT_CODE);
  return { added, fixed, customerCreated, customerFixed, linked: true };
};

/**
 * Validate that required parent account exists for an entity type
 *
 * @param {string} companyId - The company ID
 * @param {string} entityType - 'customer', 'vendor', or 'bank'
 * @returns {Promise<{exists: boolean, account: object|null, parentCode: string}>}
 */
export const validateParentAccountExists = async (companyId, entityType) => {
  const parentCode = LINKED_ACCOUNT_PARENTS[entityType];

  if (!parentCode) {
    return { exists: false, account: null, parentCode: null };
  }

  try {
    const accountsRef = collection(db, 'companies', companyId, 'accounts');
    const q = query(accountsRef, where('code', '==', parentCode));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return { exists: false, account: null, parentCode };
    }

    return {
      exists: true,
      account: { id: snapshot.docs[0].id, ...snapshot.docs[0].data() },
      parentCode
    };
  } catch (error) {
    console.error('Error validating parent account:', error);
    return { exists: false, account: null, parentCode };
  }
};

/**
 * Ensure parent account exists, create if missing
 *
 * @param {string} companyId - The company ID
 * @param {string} entityType - 'customer', 'vendor', or 'bank'
 * @param {string} userId - User performing the action
 * @returns {Promise<{success: boolean, account: object|null, message: string}>}
 */
export const ensureParentAccountExists = async (companyId, entityType, userId) => {
  // First check if it exists
  const validation = await validateParentAccountExists(companyId, entityType);

  if (validation.exists) {
    return {
      success: true,
      account: validation.account,
      message: 'Parent account exists'
    };
  }

  // Parent doesn't exist - repair COA to add it
  const repairResult = await repairChartOfAccounts(companyId, userId);

  if (!repairResult.success) {
    return {
      success: false,
      account: null,
      message: repairResult.message
    };
  }

  // Now fetch the parent account
  const recheck = await validateParentAccountExists(companyId, entityType);

  if (recheck.exists) {
    return {
      success: true,
      account: recheck.account,
      message: 'Parent account created successfully'
    };
  }

  return {
    success: false,
    account: null,
    message: 'Failed to create parent account'
  };
};

/**
 * Create a linked Level 5 account for a customer, vendor, or bank
 * Note: Opening balance is entered via Opening Balances page, not here
 *
 * @param {string} companyId - The company ID
 * @param {string} entityType - 'customer', 'vendor', or 'bank'
 * @param {string} entityId - The entity's document ID
 * @param {string} entityName - The entity's name
 * @param {string} userId - The user creating the account
 * @returns {Promise<{success: boolean, accountId: string|null, accountCode: string|null, message: string}>}
 */
export const createLinkedAccount = async (
  companyId,
  entityType,
  entityId,
  entityName,
  userId
) => {
  try {
    // Ensure parent account exists
    const parentResult = await ensureParentAccountExists(companyId, entityType, userId);

    if (!parentResult.success) {
      return {
        success: false,
        accountId: null,
        accountCode: null,
        message: parentResult.message
      };
    }

    const parentAccount = parentResult.account;
    const accountsRef = collection(db, 'companies', companyId, 'accounts');

    // Existing Level 5 accounts under this parent
    const childQuery = query(
      accountsRef,
      where('parentId', '==', parentAccount.id),
      where('level', '==', 5)
    );
    const childSnapshot = await getDocs(childQuery);

    // Take the first FREE number, not the count. Counting hands out a number
    // that is already in use the moment one child has been deleted — which is
    // how two accounts end up sharing a code and splitting a party's ledger.
    const usedNumbers = new Set();
    childSnapshot.docs.forEach(d => {
      const childCode = String(d.data().code || '');
      if (childCode.startsWith(parentAccount.code)) {
        const suffix = parseInt(childCode.slice(parentAccount.code.length), 10);
        if (!isNaN(suffix)) usedNumbers.add(suffix);
      }
    });
    let nextNumber = 1;
    while (usedNumbers.has(nextNumber)) nextNumber++;

    // Generate account code (the helper adds 1 to what it is given)
    const accountCode = generateLevel5Code(parentAccount.code, nextNumber - 1);

    // Determine account name prefix
    const namePrefix = {
      customer: 'Customer',
      vendor: 'Vendor',
      bank: 'Bank',
      staff: 'Staff'
    };

    // Build path array
    const path = [...(parentAccount.path || []), parentAccount.id];

    // Create the account (opening balance is entered via Opening Balances page)
    const timestamp = new Date().toISOString();
    const accountData = {
      code: accountCode,
      name: `${namePrefix[entityType]}: ${entityName}`,
      level: 5,
      accountType: parentAccount.accountType,
      balanceType: parentAccount.balanceType,
      parentId: parentAccount.id,
      path: path,
      isSystemAccount: false, // User-created linked accounts can be deleted if entity is deleted
      isPostable: true,
      isActive: true,
      description: `Linked ${entityType} account for ${entityName}`,
      linkedEntityType: entityType,
      linkedEntityId: entityId,
      openingBalance: 0,
      currentBalance: 0,
      createdAt: timestamp,
      updatedAt: timestamp,
      createdBy: userId
    };

    const docRef = doc(accountsRef);
    await setDoc(docRef, accountData);

    return {
      success: true,
      accountId: docRef.id,
      accountCode: accountCode,
      message: `Linked account created: ${accountCode}`
    };
  } catch (error) {
    console.error('Error creating linked account:', error);
    return {
      success: false,
      accountId: null,
      accountCode: null,
      message: `Error creating linked account: ${error.message}`
    };
  }
};

/**
 * Get the linked account for an entity
 *
 * @param {string} companyId - The company ID
 * @param {string} entityType - 'customer', 'vendor', or 'bank'
 * @param {string} entityId - The entity's document ID
 * @returns {Promise<object|null>}
 */
export const getLinkedAccount = async (companyId, entityType, entityId) => {
  try {
    const accountsRef = collection(db, 'companies', companyId, 'accounts');
    const q = query(
      accountsRef,
      where('linkedEntityType', '==', entityType),
      where('linkedEntityId', '==', entityId)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
  } catch (error) {
    console.error('Error getting linked account:', error);
    return null;
  }
};

/**
 * Ensure the Production Wastage expense account (51106001) exists for a company.
 * Called before posting a Production Receipt that records wastage, so the
 * write-off leg always has an account to hit. Reuses repairChartOfAccounts to
 * add it (and its parent) from the default chart if missing, then refreshes the
 * accounts cache so the posting engine picks it up. Safe/idempotent.
 */
export const ensureProductionWastageAccount = async (companyId, userId) => {
  try {
    // Cheap path (0 Firestore reads): the account is almost always already in the
    // device-cached chart of accounts once seeded/repaired.
    const cached = await getAccountsCached(companyId);
    if (Array.isArray(cached) && cached.some(a => a.code === '51106001')) return true;
    // Not cached — confirm with a single live read before triggering a repair.
    const accountsRef = collection(db, 'companies', companyId, 'accounts');
    const snap = await getDocs(query(accountsRef, where('code', '==', '51106001'), limit(1)));
    if (!snap.empty) return true;
    await repairChartOfAccounts(companyId, userId);
    invalidateMaster(companyId, 'accounts');
    return true;
  } catch (error) {
    console.error('ensureProductionWastageAccount failed:', error);
    return false;
  }
};

/**
 * Delete the linked COA account for a vendor/customer/bank entity.
 * Called when the entity is deleted to keep COA in sync.
 *
 * @param {string} companyId - The company ID
 * @param {string} entityType - 'vendor', 'customer', or 'bank'
 * @param {string} entityId - The entity's document ID
 * @returns {Promise<boolean>} true if deleted, false if not found
 */
export const deleteLinkedAccount = async (companyId, entityType, entityId) => {
  try {
    const account = await getLinkedAccount(companyId, entityType, entityId);
    if (!account) return false;

    const accountRef = doc(db, 'companies', companyId, 'accounts', account.id);
    await deleteDoc(accountRef);
    return true;
  } catch (error) {
    console.error('Error deleting linked account:', error);
    return false;
  }
};

/**
 * Check if COA is initialized for a company
 *
 * @param {string} companyId - The company ID
 * @returns {Promise<{initialized: boolean, hasSystemAccounts: boolean, accountCount: number}>}
 */
export const checkCOAStatus = async (companyId) => {
  try {
    const accountsRef = collection(db, 'companies', companyId, 'accounts');
    const snapshot = await getDocs(accountsRef);

    if (snapshot.empty) {
      return { initialized: false, hasSystemAccounts: false, accountCount: 0 };
    }

    // Check for system accounts
    const systemAccounts = snapshot.docs.filter(doc => doc.data().isSystemAccount);

    return {
      initialized: true,
      hasSystemAccounts: systemAccounts.length > 0,
      accountCount: snapshot.size
    };
  } catch (error) {
    console.error('Error checking COA status:', error);
    return { initialized: false, hasSystemAccounts: false, accountCount: 0 };
  }
};

/**
 * Get all Level 4 accounts that allow user accounts
 * Used for "Add Account" dropdown
 *
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>}
 */
export const getLevel4ParentsForUserAccounts = async (companyId) => {
  try {
    const accountsRef = collection(db, 'companies', companyId, 'accounts');
    const q = query(
      accountsRef,
      where('level', '==', 4),
      where('allowUserAccounts', '==', true)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => a.code.localeCompare(b.code));
  } catch (error) {
    console.error('Error getting Level 4 parents:', error);
    return [];
  }
};

/**
 * Sync opening balance to SubTransaction in OB transaction
 * This ensures opening balances are reflected in the unified transaction system
 *
 * @param {string} companyId - The company ID
 * @param {string} accountId - The account document ID
 * @param {object} accountData - Account data (code, name, accountType, balanceType)
 * @param {number} openingBalance - The opening balance amount
 * @param {string} entityType - Optional: 'customer', 'vendor', 'bank', or null for regular accounts
 * @param {string} entityId - Optional: The entity ID if linked account
 * @param {string} entityName - Optional: The entity name if linked account
 * @param {string} userId - The user performing the action
 * @returns {Promise<{success: boolean, subTransactionId: string|null, message: string}>}
 */
export const syncOpeningBalanceToSubTransaction = async (
  companyId,
  accountId,
  accountData,
  openingBalance,
  entityType = null,
  entityId = null,
  entityName = null,
  userId
) => {
  try {
    // Skip if no opening balance
    if (!openingBalance || openingBalance === 0) {
      return { success: true, subTransactionId: null, message: 'No opening balance to sync' };
    }

    // Get active financial year
    const fyRef = collection(db, 'companies', companyId, 'financialYears');
    const fyQuery = query(fyRef, where('isActive', '==', true));
    const fySnapshot = await getDocs(fyQuery);

    let fyDoc = null;

    if (fySnapshot.empty) {
      // No active FY, try to get the most recent one
      const allFyQuery = query(fyRef, orderBy('startDate', 'desc'));
      const allFySnapshot = await getDocs(allFyQuery);

      if (allFySnapshot.empty) {
        return { success: false, subTransactionId: null, message: 'No financial year found' };
      }
      fyDoc = allFySnapshot.docs[0];
    } else {
      fyDoc = fySnapshot.docs[0];
    }

    if (!fyDoc) {
      return { success: false, subTransactionId: null, message: 'No financial year found' };
    }

    const financialYear = { id: fyDoc.id, ...fyDoc.data() };

    // Get or create OB transaction
    const obTransaction = await ensureOpeningBalanceTransaction(
      companyId,
      financialYear.id,
      financialYear.startDate,
      userId
    );

    if (!obTransaction?.id) {
      return { success: false, subTransactionId: null, message: 'Failed to get OB transaction' };
    }

    // Determine debit/credit based on account type
    // Assets: Debit increases, Credit decreases (normal balance = Debit)
    // Liabilities: Credit increases, Debit decreases (normal balance = Credit)
    // Equity: Credit increases, Debit decreases (normal balance = Credit)
    let debitAmount = 0;
    let creditAmount = 0;

    const accountType = (accountData.accountType || '').toLowerCase();

    if (accountType === 'asset') {
      debitAmount = openingBalance;
    } else if (accountType === 'liability' || accountType === 'equity') {
      creditAmount = openingBalance;
    } else {
      // For revenue/expense (shouldn't have OB typically), use balance type
      if (accountData.balanceType === 'debit') {
        debitAmount = openingBalance;
      } else {
        creditAmount = openingBalance;
      }
    }

    const timestamp = new Date().toISOString();
    const subTransRef = collection(db, 'companies', companyId, 'subTransactions');

    // Check if SubTransaction already exists for this account in this OB transaction
    const existingQuery = query(
      subTransRef,
      where('transactionId', '==', obTransaction.id),
      where('accountId', '==', accountId)
    );
    const existingSnapshot = await getDocs(existingQuery);

    const subTransData = {
      transactionId: obTransaction.id,
      tranTypeCode: 'OB',
      docNumber: obTransaction.docNumber,
      docDate: obTransaction.docDate,
      financialYearId: financialYear.id,
      lineNumber: 0,
      accountId: accountId,
      accountCode: accountData.code || '',
      accountName: accountData.name || '',
      partyType: entityType,
      partyId: entityId,
      partyName: entityName,
      description: entityType
        ? `Opening Balance - ${entityName || entityType}`
        : `Opening Balance - ${accountData.name || 'Account'}`,
      debitAmount: debitAmount,
      creditAmount: creditAmount,
      status: 'Draft',
      updatedAt: timestamp,
      updatedBy: userId
    };

    let subTransactionId;

    if (!existingSnapshot.empty) {
      // Update existing
      const existingDoc = existingSnapshot.docs[0];
      subTransactionId = existingDoc.id;
      const docRef = doc(db, 'companies', companyId, 'subTransactions', existingDoc.id);
      await setDoc(docRef, subTransData, { merge: true });
    } else {
      // Create new
      subTransData.createdAt = timestamp;
      subTransData.createdBy = userId;
      const newDocRef = await addDoc(subTransRef, subTransData);
      subTransactionId = newDocRef.id;
    }

    return {
      success: true,
      subTransactionId: subTransactionId,
      message: 'Opening balance synced to SubTransaction'
    };
  } catch (error) {
    console.error('Error syncing opening balance to SubTransaction:', error);
    return {
      success: false,
      subTransactionId: null,
      message: `Error syncing opening balance: ${error.message}`
    };
  }
};

const coaInitializer = {
  initializeChartOfAccounts,
  repairChartOfAccounts,
  validateParentAccountExists,
  ensureParentAccountExists,
  createLinkedAccount,
  getLinkedAccount,
  checkCOAStatus,
  getLevel4ParentsForUserAccounts,
  syncOpeningBalanceToSubTransaction
};

export default coaInitializer;
